import { Tooltip } from 'antd';
import ShortSteps from 'common/publish-steps/containers/short-steps-container';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Button, Flex, Icon, notification, NotificationButton } from 'ui';
import { withRouter } from 'utils/withRouter';
import useDoNotShow from '../../../../../utils/hooks/useDoNotShow';
import RouteOptimisationSteps from '../../../../publish-steps/components/optimising-route-steps';
import { blockConfig, viewConfig } from '../../../config';
import { abortRouteOptimisation, retryRouteOptimisation } from '../../../containers/gantt-actions';

const Action = styled.div`
  height: 100%;
  ${p => p.width ? `width: ${p.width}px !important;` : ''}
  font-size: 12px;
  padding: 0 5px;
  overflow: hidden;
  text-align: center;
  vertical-align: middle;
  line-height: 32px;
  background-color: #F7F9FB;
  color: #5A687C;
  &:hover {
    background-color: #D9E0E6;
    ${p => p.isSteps || 'border-color: #979797'};
  }
  &:active {
    background-color: #5A687C;
    ${p => p.isSteps || 'color: white'};
  }
  cursor: pointer;
  border: 1px solid #D9E0E6;
  ${p => p.disabled && `
    cursor: not-allowed !important;
    opacity: 0.4 !important;
    background-color: #F7F9FB !important;
    color: #5A687C !important;
    border-color: #D9E0E6 !important;
  `}
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: auto;
`;

const Buttons = styled(Flex)`
  float: right;
  margin-top: 10px;
  > :first-child {
    margin-right: 5px;
  }
`;

const StyledRow = styled(Flex)`
  height: 32px;
  margin: -12px;
  margin-bottom: 10px;
`;

const ActionDescription = styled.span`
  margin-left: 6px;
`;

const ActionIcon = styled(Icon)`
  color: inherit !important;
`;

const ConfirmButton = styled(Button)`
  color: white;
  background-color: #6f7e95;
  &:hover {
    opacity: 0.7;
    color: white;
    background-color: #6f7e95;
  }
`;

export const getWarningDescription = (handleClick, intl, onClose = null) => <>
  <p data-test-id='pb-warning-desc'>
    {intl.formatMessage({ id: 'planningBoard.blocks.ADMIN_WARNING_DESCRIPTION' })}
  </p>
  <Buttons>
    <NotificationButton
      data-test-id='btn-pb-warning-cancel'
      onClick={() => {
        if (onClose) {
          onClose();
        }
        notification.destroy('assignedPlannerWarning');
      }}
    >
      {intl.formatMessage({ id: 'common.CANCEL' })}
    </NotificationButton>
    <NotificationButton primary={1} onClick={handleClick} data-test-id='btn-pb-warning-proceed'>
      {intl.formatMessage({ id: 'common.PROCEED' })}
    </NotificationButton>
  </Buttons>
</>;

const BlockDetailActions = ({
  actions,
  history,
  block,
  setIsPopoverVisible,
  planningId,
  publishToSAP,
  splitBlock,
  deletePlanning,
  markDateConfirmed,
  isDoingAction = true,
  isReservation = false,
  isAbsence = false,
  isRouteOptimisation,
  fetchBlockDataSuccess,
  updateBlockId,
  updateBlockOperationInfo,
  isAssignedPlanner,
  intl,
  markDateUnconfirmed,
  restoreBackup,
  recalculateTravelTime,
}) => {
  const dispatch = useDispatch();
  const [doNotShowReschedule, checkbox] = useDoNotShow('confirmedToTentative');
  const spanWidth = actions?.find(a => a.id === blockConfig.blockActions.showSteps) ?
    blockConfig.popoverWidth - blockConfig.stepsWidth :
    0;

  const doRedirect = () => {
    if (isReservation) {
      return history.push(`edit/reservation/${planningId}`);
    } else if (isAbsence) {
      return history.push(`edit/absence/${planningId}`);
    }
    return history.push(`edit/field_service/${planningId}`);
  };

  const doAction = (id) => {
    if (id === blockConfig.blockActions.abortRouteOptimisation) {
      dispatch(abortRouteOptimisation({
        forwardPlanningId: block?.id,
      }));
    } else if (id === blockConfig.blockActions.retryRouteOptimisation) {
      dispatch(retryRouteOptimisation({
        forwardPlanningId: block?.id,
      }));
    } else if (id === blockConfig.blockActions.editPlanning) {
      setIsPopoverVisible(false);
      doRedirect();
    } else if (id === blockConfig.blockActions.markDateConfirmed) {
      markDateConfirmed({
        boardId: viewConfig.boardIdTypes.service,
        planningId,
      });
    } else if (id === blockConfig.blockActions.publishToSAP) {
      publishToSAP({
        boardId: viewConfig.boardIdTypes.service,
        planningIds: [planningId],
      });
    } else if (id === blockConfig.blockActions.deletePlanning) {
      deletePlanning({
        boardId: viewConfig.boardIdTypes.service,
        planningId,
      });
    } else if (id === blockConfig.blockActions.viewPlanning) {
      setIsPopoverVisible(false);
      doRedirect();
    } else if (id === blockConfig.blockActions.splitBlock) {
      setIsPopoverVisible(false);
      splitBlock({
        boardId: viewConfig.boardIdTypes.service,
        planningId,
      });
    } else if (id === blockConfig.blockActions.markDateUnconfirmed) {
      markDateUnconfirmed({
        boardId: viewConfig.boardIdTypes.service,
        planningId,
        successCb: block.showAs === 'active' &&
          block.type === blockConfig.types.fieldService ?
          () => {
            if (!doNotShowReschedule) {
              notification.info({
                key: 'confirmedToTentative',
                btn: <ConfirmButton
                  type='primary'
                  size='small'
                  onClick={() => notification.destroy('confirmedToTentative')}
                >
                  {intl.formatMessage({ id: 'common.OK' })}
                </ConfirmButton>,
                message:
                  intl.formatMessage({
                    id: 'planningBoard.blocks.PLANNED_TO_TENTATIVE_TITLE',
                  }),
                description: <>
                  {intl.formatMessage({
                    id: 'planningBoard.blocks.PLANNED_TO_TENTATIVE',
                  })}
                  {checkbox}
                </>,
                duration: 0,
                placement: 'top',
                className: 'extended-notification',
              });
            }
          } :
          null,
      });
    } else if (id === blockConfig.blockActions.restoreBackup) {
      restoreBackup({
        planningId,
        boardId: viewConfig.boardIdTypes.service,
      });
    } else if (id === blockConfig.blockActions.recalculateTravelTime) {
      setIsPopoverVisible(false);
      recalculateTravelTime({
        planningId,
        boardId: viewConfig.boardIdTypes.service,
      });
    }
  };

  const handleActionClick = (id) => {
    if (!isDoingAction) {
      if (
        !isAssignedPlanner &&
        id !== blockConfig.blockActions.editPlanning &&
        id !== blockConfig.blockActions.viewPlanning
      ) {
        notification.warning({
          message: intl.formatMessage({ id: 'planningBoard.blocks.ADMIN_WARNING_TITLE' }),
          description: getWarningDescription(() => {
            notification.destroy('assignedPlannerWarning');
            doAction(id);
          }, intl),
          className: 'extended-notification',
          key: 'assignedPlannerWarning',
          duration: 0,
        });
      } else {
        doAction(id);
      }
    }
  };

  return (
    <StyledRow ffrn>
      {actions?.sort((a, b) => b.sort - a.sort)?.map((a) => {
        if (a.id === blockConfig.blockActions.showSteps) {
          return (
            <Action
              key={a.id}
              width={blockConfig.stepsWidth}
              isSteps={true}
            >
              {isRouteOptimisation ?
                <RouteOptimisationSteps
                  currentStatus={block.operationInfo}
                /> :
                <ShortSteps
                  id={planningId}
                  needsPolling={false}
                  currentBlockStatus={block?.operationInfo}
                  onDataReloaded={fetchBlockDataSuccess}
                  type='planning'
                  initialState={block.operationInfo}
                  updateId={(from, to) => updateBlockId({ from, to, laneId: block.laneId })}
                  closePopover={() => setIsPopoverVisible(false)}
                  openSidepanel={doRedirect}
                  updateBlockOperationInfo={
                    (currentStatus, id) =>
                      updateBlockOperationInfo({ currentStatus, id, laneId: block.laneId })
                  }
                />}
            </Action>
          );
        }
        return (
          <Tooltip
            title={a.description}
            key={a.id}
            placement='top'
            mouseEnterDelay={0.5}
            getPopupContainer={node => node.parentNode}
            overlayClassName='detail-actions-tooltip'
          >
            <Action
              key={a.id}
              width={spanWidth}
              onClick={() => handleActionClick(a.id)}
              disabled={isDoingAction}
              data-test-id={`block-action-${a.id}`}
            >
              <ActionIcon type={a.icon} solid={a.id === blockConfig.blockActions.splitBlock} />
              <ActionDescription>{a.description}</ActionDescription>
            </Action>
          </Tooltip>
        );
      },
      )}
    </StyledRow>
  );
};

BlockDetailActions.propTypes = {
  actions: PropTypes.array,
  history: PropTypes.object,
  setIsPopoverVisible: PropTypes.func,
  planningId: PropTypes.string,
  publishToSAP: PropTypes.func,
  markDateConfirmed: PropTypes.func,
  isDoingAction: PropTypes.bool,
  isReservation: PropTypes.bool,
  isAbsence: PropTypes.bool,
  isRouteOptimisation: PropTypes.bool,
  deletePlanning: PropTypes.func,
  block: PropTypes.object,
  fetchBlockDataSuccess: PropTypes.func,
  updateBlockId: PropTypes.func,
  updateBlockOperationInfo: PropTypes.func,
  splitBlock: PropTypes.func,
  isAssignedPlanner: PropTypes.bool,
  intl: PropTypes.object,
  markDateUnconfirmed: PropTypes.func,
  restoreBackup: PropTypes.func,
  recalculateTravelTime: PropTypes.func,
};

export default injectIntl(withRouter(BlockDetailActions));
